import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Guest User Daily Quiz Limit Service
 *
 * Tracks daily quiz limits for guest users (3 per day)
 * Uses AsyncStorage for persistence
 */

export interface GuestDailyLimit {
  date: string; // YYYY-MM-DD
  quizCount: number;
  lastQuizAt: string;
}

const GUEST_LIMIT_KEY = '@guest_daily_limit';
const DAILY_LIMIT = 7;

export class GuestLimitService {
  /**
   * Get current guest daily usage
   */
  async getGuestDailyUsage(): Promise<{
    dailyQuizLimit: number;
    currentUsage: number;
    remainingQuizzes: number;
    resetTime: Date;
  }> {
    try {
      const today = new Date().toISOString().split('T')[0];
      const data = await AsyncStorage.getItem(GUEST_LIMIT_KEY);

      if (!data) {
        // No data yet - fresh start
        return {
          dailyQuizLimit: DAILY_LIMIT,
          currentUsage: 0,
          remainingQuizzes: DAILY_LIMIT,
          resetTime: this.getNextDayResetTime()
        };
      }

      const guestLimit: GuestDailyLimit = JSON.parse(data);

      // Check if data is from today
      if (guestLimit.date !== today) {
        // New day - reset counter
        await this.resetGuestLimit();
        return {
          dailyQuizLimit: DAILY_LIMIT,
          currentUsage: 0,
          remainingQuizzes: DAILY_LIMIT,
          resetTime: this.getNextDayResetTime()
        };
      }

      // Return current day's usage
      const remaining = Math.max(0, DAILY_LIMIT - guestLimit.quizCount);
      return {
        dailyQuizLimit: DAILY_LIMIT,
        currentUsage: guestLimit.quizCount,
        remainingQuizzes: remaining,
        resetTime: this.getNextDayResetTime()
      };
    } catch (error) {
      console.error('[GuestLimit] Error getting guest daily usage:', error);
      // Return default on error
      return {
        dailyQuizLimit: DAILY_LIMIT,
        currentUsage: 0,
        remainingQuizzes: DAILY_LIMIT,
        resetTime: this.getNextDayResetTime()
      };
    }
  }

  /**
   * Check if guest can start another quiz
   */
  async canGuestStartQuiz(): Promise<boolean> {
    const usage = await this.getGuestDailyUsage();
    return usage.remainingQuizzes > 0;
  }

  /**
   * Record a completed quiz for guest user
   */
  async recordGuestQuizCompletion(): Promise<void> {
    try {
      const today = new Date().toISOString().split('T')[0];
      const data = await AsyncStorage.getItem(GUEST_LIMIT_KEY);

      let guestLimit: GuestDailyLimit;

      if (!data) {
        // First quiz of the day
        guestLimit = {
          date: today,
          quizCount: 1,
          lastQuizAt: new Date().toISOString()
        };
      } else {
        guestLimit = JSON.parse(data);

        // Check if it's a new day
        if (guestLimit.date !== today) {
          // Reset for new day
          guestLimit = {
            date: today,
            quizCount: 1,
            lastQuizAt: new Date().toISOString()
          };
        } else {
          // Same day - increment
          guestLimit.quizCount += 1;
          guestLimit.lastQuizAt = new Date().toISOString();
        }
      }

      await AsyncStorage.setItem(GUEST_LIMIT_KEY, JSON.stringify(guestLimit));
      console.log(`[GuestLimit] Recorded quiz completion. Count: ${guestLimit.quizCount}/${DAILY_LIMIT}`);
    } catch (error) {
      console.error('[GuestLimit] Error recording guest quiz completion:', error);
    }
  }

  /**
   * Reset guest limit (for new day or testing)
   */
  async resetGuestLimit(): Promise<void> {
    try {
      await AsyncStorage.removeItem(GUEST_LIMIT_KEY);
      console.log('[GuestLimit] Guest limit reset');
    } catch (error) {
      console.error('[GuestLimit] Error resetting guest limit:', error);
    }
  }

  /**
   * Get formatted limit message
   */
  async getGuestLimitMessage(): Promise<string> {
    const usage = await this.getGuestDailyUsage();

    if (usage.remainingQuizzes > 0) {
      return `${usage.remainingQuizzes} quiz${usage.remainingQuizzes !== 1 ? 'zes' : ''} remaining today`;
    } else {
      const resetTime = usage.resetTime.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
      return `Great session! More quizzes at ${resetTime}`;
    }
  }

  /**
   * Calculate next day reset time (midnight)
   */
  private getNextDayResetTime(): Date {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  }
}

// Singleton instance
export const guestLimitService = new GuestLimitService();
export default guestLimitService;
